import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import Layout from '../components/layout/Layout';
import { API_BASE } from '../api';

function ClientTasks() {
  const [clientTasks, setClientTasks] = useState([]);
  const [clientInfo, setClientInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { currentTheme } = useContext(ThemeContext);

  const { clientId, clientName } = location.state || {};

  const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'hidden'
    },
    animatedBackground: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' },
    floatingOrb: { position: 'absolute', borderRadius: '50%', filter: 'blur(40px)', opacity: 0.3, animation: 'float 8s ease-in-out infinite' },
    orb1: { width: '300px', height: '300px', background: colors.primary, top: '10%', left: '5%' },
    orb2: { width: '200px', height: '200px', background: colors.secondary, top: '60%', right: '10%' },
    orb3: { width: '250px', height: '250px', background: colors.accent, bottom: '20%', left: '15%' },
    orb4: { width: '180px', height: '180px', background: colors.success, top: '30%', right: '20%' },
    gridOverlay: {
      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
      backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
      backgroundSize: '50px 50px'
    },
    header: { padding: '30px 30px 20px', position: 'relative', zIndex: 2 },
    headerContent: { maxWidth: '1200px', margin: '0 auto' },
    greeting: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '12px' },
    pageTitle: { fontSize: '2.2rem', fontWeight: '800', background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 },
    welcomeBadge: { 
      background: 'rgba(255, 255, 255, 0.1)', 
      padding: '6px 12px', 
      borderRadius: '16px', 
      border: `1px solid ${colors.borderDark}`, 
      display: 'flex', 
      alignItems: 'center', 
      gap: '6px' 
    },
    badgeText: { color: 'white', fontWeight: '600', fontSize: '0.8rem' },
    badgeRole: { background: colors.primary, color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '0.65rem', fontWeight: '600' },
    pageSubtitle: { color: colors.textLight, fontSize: '1rem', margin: 0 },
    content: { padding: '0 30px 30px', maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 },
    formCard: { 
      background: 'rgba(255, 255, 255, 0.05)', 
      borderRadius: '20px', 
      border: `1px solid ${colors.borderDark}`, 
      overflow: 'hidden', 
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)' 
    },
    cardHeader: { 
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, 
      color: 'white', 
      padding: '20px 25px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '10px'
    },
    cardTitle: { fontSize: '1.5rem', fontWeight: '700', margin: 0 },
    backBtn: { 
      background: 'rgba(255, 255, 255, 0.1)', 
      color: 'white', 
      border: `1px solid ${colors.borderDark}`, 
      padding: '10px 18px', 
      borderRadius: '10px', 
      fontWeight: '600', 
      fontSize: '0.9rem', 
      cursor: 'pointer', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '6px', 
      textDecoration: 'none', 
      marginBottom: '15px' 
    },
    refreshBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '8px 16px',
      borderRadius: '10px',
      fontWeight: '600',
      fontSize: '0.85rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.3s ease'
    },
    downloadBtn: {
      background: `linear-gradient(135deg, ${colors.success}, #059669)`,
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '10px',
      fontWeight: '600',
      fontSize: '0.85rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.3s ease'
    },
    loadingState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 30px', color: 'white' },
    spinner: { width: '18px', height: '18px', border: '2px solid transparent', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    tableHeader: { 
      background: 'rgba(255, 255, 255, 0.1)', 
      color: 'white', 
      borderBottom: `2px solid ${colors.borderDark}`, 
      padding: '10px 8px', 
      fontWeight: '600', 
      fontSize: '0.8rem',
      whiteSpace: 'nowrap'
    },
    tableRow: { borderBottom: '1px solid rgba(255, 255, 255, 0.05)', color: 'white', fontSize: '0.8rem' },
    tableCell: { 
      padding: '10px 8px', 
      verticalAlign: 'middle',
      fontSize: '0.8rem',
      whiteSpace: 'nowrap'
    },
    actionBtn: { 
      background: 'rgba(255, 255, 255, 0.1)', 
      color: 'white', 
      border: `1px solid ${colors.borderDark}`, 
      padding: '8px 15px', 
      borderRadius: '10px', 
      fontSize: '0.8rem', 
      cursor: 'pointer', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '6px' 
    },
    primaryBtn: { 
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, 
      color: 'white', 
      border: 'none', 
      padding: '10px 18px', 
      borderRadius: '10px', 
      fontWeight: '600', 
      fontSize: '0.9rem', 
      cursor: 'pointer', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '6px' 
    },
    smallBadge: { padding: '3px 8px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: '600' },
    linkButton: { 
      background: 'rgba(255, 255, 255, 0.1)', 
      color: 'white', 
      border: `1px solid ${colors.borderDark}`, 
      padding: '4px 8px', 
      borderRadius: '6px', 
      cursor: 'pointer', 
      fontSize: '0.7rem' 
    },
    videoTypeBadge: { padding: '3px 6px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '500' },
    tableNameBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '500',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)'
    },
    filterBadge: {
      background: colors.accent,
      color: 'white',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '500',
      marginLeft: '10px'
    }
  };

  useEffect(() => {
    if (clientId || clientName) fetchClientDetails();
  }, [clientId, clientName]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/get_client_details.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId ? parseInt(clientId) : null, client_name: clientName })
      });
      const data = await response.json();
      if (data.success) {
        setClientInfo(data.client);
        setClientTasks(data.tasks);
      }
    } catch (error) { 
      console.error('Error loading client details:', error);
      setError('Error loading client details: ' + error.message);
    } finally { 
      setLoading(false); 
    }
  };

  const isValidLink = (link) => {
    if (!link || typeof link !== 'string') return false;
    return !['-', 'Open', 'NULL', 'null', '', 'View', 'N/A'].includes(link.trim());
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { class: 'bg-success text-white', text: 'Completed', icon: 'fa-check' },
      approved: { class: 'bg-success text-white', text: 'Approved', icon: 'fa-check-circle' },
      assigned: { class: 'bg-primary text-white', text: 'Assigned', icon: 'fa-user-check' },
      pending: { class: 'bg-warning text-dark', text: 'Pending', icon: 'fa-clock' },
      in_progress: { class: 'bg-info text-white', text: 'In Progress', icon: 'fa-spinner' },
      'needs_revision': { class: 'bg-danger text-white', text: 'Needs Revision', icon: 'fa-exclamation-circle' }
    };
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    return <span className={`badge ${config.class}`} style={styles.smallBadge}><i className={`fas ${config.icon} me-1`}></i>{config.text}</span>;
  };

  const getEditedVideoLink = (task) => task.edited_video_link || task.edited_link || '';

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'NULL') return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const truncateText = (text, maxLength = 15) => {
    if (!text || text === '-' || text === 'NULL') return '-';
    return text.length <= maxLength ? text : text.substring(0, maxLength) + '...';
  };

  const getTableNameColor = (tableName) => {
    switch(tableName) {
      case 'tasks': return colors.primary;
      case 'editor_tasks': return colors.secondary;
      default: return colors.accent;
    }
  };

  const downloadCSV = () => {
    if (!clientTasks.length) return;

    const headers = [
      'Table',
      'ID',
      'Video Name',
      'Video Type',
      'Editor',
      'Designer',
      'Raw Link',
      'Count',
      'Shoot Date',
      'Status',
      'Edited Link',
      'Thumbnail Link',
      'Reference Link'
    ];

    const csvRows = clientTasks.map(task => {
      const editedVideoLink = getEditedVideoLink(task);
      const row = [
        task.table_name || 'tasks',
        task.id ?? '',
        task.video_name ?? 'N/A',
        task.video_type ?? '',
        task.editor_name ?? 'Unassigned',
        task.designer_name ?? 'Unassigned',
        task.raw_link ?? 'N/A',
        task.video_count ?? 0,
        task.shoot_date ?? 'N/A',
        task.status ?? 'pending',
        editedVideoLink || 'N/A',
        task.thumbnail_link || 'N/A',
        task.reference_video_link || 'N/A'
      ];

      return row.map(cell => {
        const value = cell === null || cell === undefined ? '' : String(cell);
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',');
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${(clientInfo?.name || clientName || 'client').replace(/\s+/g, '_')}_all_tasks_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Count tasks by table
  const tasksByTable = clientTasks.reduce((acc, task) => {
    const tableName = task.table_name || 'tasks';
    acc[tableName] = (acc[tableName] || 0) + 1;
    return acc;
  }, {});

  if (loading) return <Layout><div style={styles.container}><div style={styles.loadingState}>Loading...</div></div></Layout>;

  return (
    <Layout>
      <div style={styles.container}>
        <div className="animated-background" style={styles.animatedBackground}>
          <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
          <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
          <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
          <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
          <div className="grid-overlay" style={styles.gridOverlay}></div>
        </div>

        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.greeting}>
              <h1 style={styles.pageTitle}>Client Tasks</h1>
              <div style={styles.welcomeBadge}>
                <span style={styles.badgeText}>Tables</span>
                <span style={styles.badgeRole}>tasks + editor_tasks</span>
              </div>
            </div>
            <p style={styles.pageSubtitle}>All tasks for {clientInfo?.name || clientName || 'Client'}</p>
          </div>
        </div>

        <div style={styles.content}>
          <div style={{display: 'flex', gap: '12px', marginBottom: '15px', flexWrap: 'wrap'}}>
            <button style={styles.backBtn} onClick={() => navigate(-1)}>
              <i className="fas fa-arrow-left me-2"></i>Back
            </button>
            <button style={styles.refreshBtn} onClick={fetchClientDetails}>
              <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-2`}></i>Refresh
            </button>
            <button style={styles.downloadBtn} onClick={downloadCSV} disabled={!clientTasks.length}>
              <i className="fas fa-file-csv me-2"></i>Download CSV
            </button>
          </div>

          <div className="tasks-card" style={styles.formCard}>
            <div style={styles.cardHeader}>
              <div>
                <h3 style={styles.cardTitle}>{clientInfo?.name || clientName} - All Tasks List</h3>
                <div style={{display: 'flex', gap: '10px', marginTop: '5px', flexWrap: 'wrap'}}>
                  <span style={{fontSize: '0.85rem', opacity: 0.9}}>
                    {clientTasks.length} total tasks
                  </span>
                  {Object.entries(tasksByTable).map(([tableName, count]) => (
                    <span key={tableName} style={styles.filterBadge}>
                      {tableName}: {count}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div style={{overflowX: 'auto'}}>
              <table className="table table-hover mb-0" style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem'}}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Table</th>
                    <th style={styles.tableHeader}>ID</th>
                    <th style={styles.tableHeader}>Video</th>
                    <th style={styles.tableHeader}>Video Type</th>
                    <th style={styles.tableHeader}>Editor</th>
                    <th style={styles.tableHeader}>Designer</th>
                    <th style={styles.tableHeader}>Raw</th>
                    <th style={styles.tableHeader}>#</th>
                    <th style={styles.tableHeader}>Shoot Date</th>
                    <th style={styles.tableHeader}>Ref</th>
                    <th style={styles.tableHeader}>Status</th>
                    <th style={styles.tableHeader}>Edited Link</th>
                    <th style={styles.tableHeader}>Thumbnail</th>
                  </tr>
                </thead>
                <tbody>
                  {clientTasks.map((task) => {
                    const editedVideoLink = getEditedVideoLink(task);
                    const tableName = task.table_name || 'tasks';
                    
                    return (
                      <tr key={`${tableName}-${task.id}`} style={styles.tableRow}>
                        {/* Table Name Column */}
                        <td style={{
                          ...styles.tableCell, 
                          fontWeight: '600',
                          color: getTableNameColor(tableName)
                        }}>
                          {tableName}
                        </td>
                        
                        {/* ID */}
                        <td style={{...styles.tableCell, fontWeight: 'bold', color: '#FFFFFF'}}>
                          #{task.id}
                          {tableName === 'editor_tasks' && (
                            <small style={{color: colors.accent, marginLeft: '5px', fontSize: '0.7rem'}}></small>
                          )}
                        </td>
                        
                        {/* Video Name */}
                        <td style={styles.tableCell}>
                          <button 
                            className="btn btn-link p-0 text-decoration-none border-0 bg-transparent"
                            onClick={() => navigate('/task-details', { state: { task } })}
                            style={{ color: '#FFFFFF', fontWeight: '600', textAlign: 'left', fontSize: '0.85rem' }}
                          >
                            {truncateText(task.video_name || 'N/A', 12)}
                          </button>
                        </td>
                        
                        {/* Video Type */}
                        <td style={styles.tableCell}>
                          {task.video_type ? (
                            <span style={{...styles.videoTypeBadge, color: 'white'}}>
                              {truncateText(task.video_type, 10)}
                            </span>
                          ) : '-'}
                        </td>
                        
                        {/* Editor */}
                        <td style={styles.tableCell}>
                          <button 
                            className="btn btn-link p-0 text-decoration-none border-0 bg-transparent"
                            onClick={() => navigate('/editor-tasks', { state: { editorId: task.editor_id, editorName: task.editor_name } })}
                            style={{ 
                              color: '#FFFFFF', 
                              fontWeight: '600', 
                              textAlign: 'left', 
                              fontSize: '0.85rem' 
                            }}
                            disabled={!task.editor_id}
                          >
                            {truncateText(task.editor_name || 'Unassigned', 8)}
                          </button>
                        </td>
                        
                        {/* Designer */}
                        <td style={styles.tableCell}>
                          <button 
                            className="btn btn-link p-0 text-decoration-none border-0 bg-transparent"
                            onClick={() => navigate('/designer-task', { state: { designerId: task.designer_id, designerName: task.designer_name } })}
                            style={{ 
                              color: '#FFFFFF', 
                              fontWeight: '600', 
                              textAlign: 'left', 
                              fontSize: '0.85rem' 
                            }}
                            disabled={!task.designer_id}
                          >
                            {truncateText(task.designer_name || 'Unassigned', 8)}
                          </button>
                        </td>
                        
                        {/* Raw Link */}
                        <td style={styles.tableCell}>
                          <button style={{
                            ...styles.linkButton, 
                            background: isValidLink(task.raw_link) ? colors.primary : 'gray'
                          }} onClick={() => isValidLink(task.raw_link) && window.open(task.raw_link, '_blank')}>
                            <i className="fas fa-external-link-alt"></i>
                          </button>
                        </td>
                        
                        {/* Count */}
                        <td style={styles.tableCell}>
                          <span className="badge" style={{
                            background: colors.accent, 
                            color: 'white', 
                            ...styles.smallBadge
                          }}>
                            {task.video_count || '0'}
                          </span>
                        </td>
                        
                        {/* Shoot Date */}
                        <td style={styles.tableCell}>
                          <small style={{ color: colors.textLight, fontSize: '0.8rem' }}>
                            {formatDate(task.shoot_date)}
                          </small>
                        </td>
                        
                        {/* Reference Link */}
                        <td style={styles.tableCell}>
                          <button style={{
                            ...styles.linkButton, 
                            background: isValidLink(task.reference_video_link) ? colors.accent : 'gray'
                          }} onClick={() => isValidLink(task.reference_video_link) && window.open(task.reference_video_link, '_blank')}>
                            <i className="fas fa-external-link-alt"></i>
                          </button>
                        </td>
                        
                        {/* Status */}
                        <td style={styles.tableCell}>
                          {getStatusBadge(task.status)}
                        </td>
                        
                        {/* Edited Link */}
                        <td style={styles.tableCell}>
                          <button style={{
                            ...styles.linkButton, 
                            background: isValidLink(editedVideoLink) ? colors.success : 'gray'
                          }} onClick={() => isValidLink(editedVideoLink) && window.open(editedVideoLink, '_blank')}>
                            <i className="fas fa-external-link-alt"></i>
                          </button>
                        </td>
                        
                        {/* Thumbnail Link */}
                        <td style={styles.tableCell}>
                          <button style={{
                            ...styles.linkButton, 
                            background: isValidLink(task.thumbnail_link) ? colors.warning : 'gray'
                          }} onClick={() => isValidLink(task.thumbnail_link) && window.open(task.thumbnail_link, '_blank')}>
                            <i className="fas fa-image"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes float { 0%, 100% { transform: translate(0px, 0px); } 50% { transform: translate(10px, -10px); } }
        .table-hover tbody tr:hover { background: rgba(255, 255, 255, 0.05) !important; }
        .badge { font-size: 0.8rem; padding: 4px 8px; }
      `}</style>
    </Layout>
  );
}

export default ClientTasks;